import type { TechnicianAssignmentEntity } from '@packages/domain/techline/assignments';

import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { RefreshAssignments, ToggleCompletion } from '@packages/domain/techline/assignments';
import { container } from '@web/services/container';
import { useCallback, useEffect, useState } from 'react';

const logger = getLogger('techline');

// Operations are prefixed with $ to make them visually distinct in this file.
const [$refreshAssignments, $toggleAssignmentCompletion] = container.resolveOperations(
    RefreshAssignments,
    ToggleCompletion
);

/**
 * Manage technician assignments for a techline case: fetching and
 * toggling completion status. All API interactions are delegated to
 * use cases; this hook owns only React state and lifecycle.
 * When isEnabled is false, the initial fetch is skipped entirely.
 *
 * @param caseNumber - the case to manage assignments for
 * @param isEnabled - whether the hook should fetch data on mount
 * @param existingAssignments - fallback assignments when the API returns an empty set
 * @returns assignmentData state and mutation functions
 */
export function useAssignments(
    caseNumber: string | undefined,
    isEnabled: boolean,
    existingAssignments?: TechnicianAssignmentEntity[]
) {
    const { account } = useAuthState();
    const [assignmentData, setAssignmentData] = useState<TechnicianAssignmentEntity[] | undefined>();
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

    /**
     * Fetch assignments from the API and update local assignmentData state.
     * Falls back to existingAssignments when the response is empty.
     */
    const refreshAssignments = useCallback(async () => {
        if (!caseNumber) {
            return;
        }

        setIsLoadingAssignments(true);
        try {
            const result = await $refreshAssignments({ caseNumber, fallbackAssignments: existingAssignments });
            setAssignmentData(result);
        } catch (error) {
            logger.error(`Failed to fetch case technician assignments [${caseNumber}]`, { caseNumber, error });
        } finally {
            setIsLoadingAssignments(false);
        }
    }, [caseNumber, existingAssignments]);

    /**
     * Toggle an assignment's completion status, then refresh the list.
     *
     * @param assignmentId - the ID of the assignment to toggle
     */
    const toggleAssignmentCompletion = useCallback(
        async (assignmentId: string) => {
            if (!assignmentData) {
                return;
            }

            try {
                await $toggleAssignmentCompletion({
                    assignmentId,
                    assignments: assignmentData,
                    account: account ?? undefined,
                });
                await refreshAssignments();
            } catch (error) {
                logger.error(`Failed to update assignment completion status [${assignmentId}]`, {
                    assignmentId,
                    error,
                });
            }
        },
        [assignmentData, account, refreshAssignments]
    );

    /** Fetch assignments on mount when enabled and a case number is available. */
    useEffect(() => {
        if (isEnabled) {
            refreshAssignments();
        }
    }, [isEnabled, refreshAssignments]);

    return {
        /** The current list of technician assignments for the case. */
        assignmentData,

        /** Whether assignments are currently being fetched. */
        isLoadingAssignments,

        /** Re-fetch assignments from the API and update local state. */
        refreshAssignments,

        /** Toggle an assignment's completion status, then refresh the list. */
        toggleAssignmentCompletion,
    };
}
